import React, { useState, useEffect } from 'react';
import ExportCsvButton from '../components/ExportCsvButton';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  ChartBarIcon, 
  MagnifyingGlassIcon,
  EyeIcon,
  TrophyIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import FAQSection from '../components/FAQSection';
import axios from 'axios';
import toast from 'react-hot-toast';

const SERPAnalysisPage = () => {
  const [keywords, setKeywords] = useState('');
  const [location, setLocation] = useState('United States');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Auto-load saved SERP analysis results on page mount
  useEffect(() => {
    const loadSavedResults = async () => {
      try {
        const response = await axios.get('/api/serp-analysis/saved-results');
        if (response.data.success) {
          setResults(response.data);
          if (response.data.keywords) {
            setKeywords(response.data.keywords);
          }
          console.log('Loaded saved SERP analysis from', response.data.lastAnalyzed);
        }
      } catch (err) {
        // No saved results, that's fine
        console.log('No saved SERP results to load');
      }
    };
    loadSavedResults();
  }, []);


  const handleAnalysis = async () => {
    if (!keywords.trim()) {
      toast.error('Please enter keywords to analyze');
      return;
    }

    const keywordArray = keywords.split(',').map(k => k.trim()).filter(k => k);
    
    if (keywordArray.length === 0) {
      toast.error('Please enter valid keywords');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/serp-analysis/analyze', {
        keywords: keywordArray,
        location
      });

      if (response.data.success) {
        setResults(response.data);
        toast.success(`Analyzed ${keywordArray.length} keywords successfully`);
      } else {
        toast.error('Analysis failed');
      }
    } catch (error) {
      console.error('SERP analysis error:', error);
      toast.error(error.response?.data?.message || 'Failed to analyze keywords');
    } finally {
      setLoading(false);
    }
  };

  const getSERPFeatureIcon = (feature) => {
    const iconMap = {
      featuredSnippet: DocumentTextIcon,
      peopleAlsoAsk: MagnifyingGlassIcon,
      localPack: EyeIcon,
      knowledgePanel: CheckCircleIcon,
      imagesPack: CheckCircleIcon,
      videos: CheckCircleIcon,
      news: CheckCircleIcon,
      shopping: CheckCircleIcon
    };
    return iconMap[feature] || CheckCircleIcon;
  };

  const getSERPFeatureName = (feature) => {
    const nameMap = {
      featuredSnippet: 'Featured Snippet',
      peopleAlsoAsk: 'People Also Ask',
      localPack: 'Local Pack',
      knowledgePanel: 'Knowledge Panel',
      imagesPack: 'Images Pack',
      videos: 'Video Results',
      news: 'News Results',
      shopping: 'Shopping Results',
      siteLinks: 'Site Links',
      reviews: 'Reviews & Ratings'
    };
    return nameMap[feature] || feature;
  };


  const SERP_FAQ = [
    { q: "What is SERP analysis and why is it important for SEO?", a: "SERP (Search Engine Results Page) analysis examines the composition, features, and ranking patterns of search results for specific keywords. It reveals what types of content Google prioritizes including featured snippets, People Also Ask boxes, knowledge panels, local packs, and video carousels. Understanding SERP composition helps you create content that matches search intent and targets the most impactful SERP features for maximum visibility." },
    { q: "How does SERPfinity analyze search engine results pages?", a: "SERPfinity performs deep SERP analysis by scanning all organic and paid results, SERP features, and AI-generated content for your target keywords. Our platform identifies ranking opportunities, tracks SERP feature ownership, analyzes competitor content strategies, and monitors changes in search intent over time. We provide actionable insights on content gaps, featured snippet opportunities, and optimal content formats for each keyword." },
    { q: "What SERP features does SERPfinity track?", a: "SERPfinity tracks all major SERP features including Featured Snippets, People Also Ask (PAA), Knowledge Panels, Local Packs, Image Packs, Video Carousels, Top Stories, Shopping Results, Site Links, and Google AI Overviews. We also monitor SERP volatility, position zero opportunities, and rich result eligibility based on your current schema markup implementation." },
    { q: "How can SERP analysis improve my content strategy?", a: "SERP analysis reveals the exact content format, depth, and structure that ranks for your target keywords. By analyzing top-ranking pages, you can identify the optimal word count, heading structure, topic coverage, and media requirements. It also uncovers content gaps where no current result fully addresses user intent, creating opportunities for your content to capture featured snippets and top rankings." },
    { q: "What is SERP volatility and how does it affect rankings?", a: "SERP volatility measures the frequency and magnitude of ranking changes for a set of keywords. High volatility indicates Google is actively testing different results or has made algorithm updates, while low volatility suggests stable rankings. Monitoring SERP volatility helps you understand when ranking changes are due to your SEO efforts versus broader algorithm shifts, allowing you to respond strategically." },
    { q: "How often should I perform SERP analysis for my keywords?", a: "For high-priority keywords driving significant traffic and revenue, weekly SERP analysis is recommended. For broader keyword sets, bi-weekly or monthly analysis is sufficient. SERPfinity automates this process with continuous monitoring and alerts when significant SERP changes occur, ensuring you never miss critical ranking opportunities or threats from competitors." }
  ];

  return (
      <>
        <Helmet>
          <title>SERP Analysis Tool | Search Results Page Analyzer | SERPfinity</title>
          <meta name="description" content="Analyze search engine results pages with SERPfinity. Track SERP features, identify ranking opportunities, monitor competitors, and optimize your content strategy for maximum search visibility." />
          <meta name="keywords" content="SERP analysis tool, search results analyzer, SERP features tracker, featured snippet optimizer, People Also Ask, SERP volatility, keyword SERP analysis, competitive SERP analysis, SERPfinity" />
          <link rel="canonical" href="https://thedigitalhall.club/serp-analysis" />
          <meta property="og:title" content="SERP Analysis Tool | SERPfinity" />
          <meta property="og:description" content="Deep SERP analysis to identify ranking opportunities, track features, and outperform competitors in search results." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://thedigitalhall.club/serp-analysis" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="SERP Analysis Tool | SERPfinity" />
          <meta name="twitter:description" content="Comprehensive SERP analysis for SEO professionals. Track features, monitor competitors, optimize rankings." />
          <meta name="robots" content="index, follow" />
          <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":SERP_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <ChartBarIcon className="w-5 h-5 text-yellow-300" />
                <span className="text-sm font-medium">Advanced SERP Tracking</span>
              </div>
              
              <h1 className="text-5xl font-bold mb-6">
                <ExportCsvButton data={results} filename="serp-analysis" />
          SERP Feature Analysis
              </h1>
              
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Track and monitor your presence across 20+ SERP features including featured snippets, 
                People Also Ask boxes, knowledge panels, local packs, and more.
              </p>

              <div className="max-w-2xl mx-auto">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="text"
                    placeholder="Enter keywords (comma-separated)"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    className="flex-1 px-6 py-4 text-gray-900 rounded-lg border-0 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                  />
                  <select
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="px-6 py-4 text-gray-900 rounded-lg border-0 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                  >
                    <option value="United States">United States</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                  </select>
                  <button
                    onClick={handleAnalysis}
                    disabled={loading}
                    className="px-8 py-4 bg-yellow-400 hover:bg-yellow-500 disabled:bg-yellow-300 text-gray-900 font-bold rounded-lg transition-all transform hover:scale-105 disabled:transform-none"
                  >
                    {loading ? 'Analyzing...' : 'Analyze SERP'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {results && (
          <section className="py-12">
            <div className="container mx-auto px-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Keywords Analyzed</p>
                      <p className="text-2xl font-bold text-gray-900">{results.summary.totalKeywords}</p>
                    </div>
                    <MagnifyingGlassIcon className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Features Found</p>
                      <p className="text-2xl font-bold text-gray-900">{results.summary.featuresFound ?? (results.summary.topFeatures || []).filter(x => x.count > 0).length}</p>
                    </div>
                    <TrophyIcon className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg Difficulty</p>
                      <p className="text-2xl font-bold text-gray-900">{results.summary.averageDifficulty ?? '—'}</p>
                    </div>
                    <ChartBarIcon className="w-8 h-8 text-orange-600" />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Top Features</p>
                      <p className="text-2xl font-bold text-gray-900">{results.summary.topFeatures.length}</p>
                    </div>
                    <ArrowTrendingUpIcon className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="bg-white rounded-lg shadow-lg mb-6">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`py-4 px-6 border-b-2 font-medium text-sm ${
                        activeTab === 'overview'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setActiveTab('features')}
                      className={`py-4 px-6 border-b-2 font-medium text-sm ${
                        activeTab === 'features'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      SERP Features
                    </button>
                    <button
                      onClick={() => setActiveTab('keywords')}
                      className={`py-4 px-6 border-b-2 font-medium text-sm ${
                        activeTab === 'keywords'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Keyword Details
                    </button>
                  </nav>
                </div>

                <div className="p-6">
                  {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Top SERP Features</h3>
                        <div className="space-y-4">
                          {results.summary.topFeatures.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                                </div>
                                <span className="font-medium text-gray-900">{getSERPFeatureName(item.feature)}</span>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-bold text-gray-900">{item.count} keywords</div>
                                <div className="text-sm text-gray-600">{item.percentage}% presence</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Analysis Summary</h3>
                        <div className="space-y-4">
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <CheckCircleIcon className="w-5 h-5 text-green-600" />
                              <span className="font-medium text-green-900">Opportunities Found</span>
                            </div>
                            <p className="text-sm text-green-800">
                              {(results.summary.topFeatures || []).filter(x => x.count > 0).length} SERP feature types detected across your keywords. Focus on optimizing for featured snippets and People Also Ask boxes.
                            </p>
                          </div>
                          
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <ChartBarIcon className="w-5 h-5 text-blue-600" />
                              <span className="font-medium text-blue-900">Difficulty Analysis</span>
                            </div>
                            <p className="text-sm text-blue-800">
                              {results.summary.averageDifficulty != null ? `Average keyword difficulty is ${results.summary.averageDifficulty}/100 (DataForSEO). Consider targeting long-tail variations for better opportunities.` : 'Keyword difficulty was not available for these keywords.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'features' && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-6">SERP Features Breakdown</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                          'featuredSnippet',
                          'peopleAlsoAsk', 
                          'localPack',
                          'knowledgePanel',
                          'imagesPack',
                          'videos',
                          'news',
                          'shopping'
                        ].map((feature) => {
                          const Icon = getSERPFeatureIcon(feature);
                          const present = results.summary.topFeatures.find(f => f.feature === feature);
                          
                          return (
                            <div key={feature} className="p-6 bg-white border border-gray-200 rounded-lg">
                              <div className="flex items-center gap-3 mb-3">
                                <Icon className="w-6 h-6 text-blue-600" />
                                <h4 className="font-medium text-gray-900">{getSERPFeatureName(feature)}</h4>
                              </div>
                              
                              {present ? (
                                <div>
                                  <div className="text-2xl font-bold text-green-600 mb-1">{present.count}</div>
                                  <div className="text-sm text-gray-600">{present.percentage}% of keywords</div>
                                  <div className="mt-3 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                                    Present
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="text-2xl font-bold text-gray-400 mb-1">0</div>
                                  <div className="text-sm text-gray-600">Not found</div>
                                  <div className="mt-3 text-xs text-gray-700 bg-gray-50 px-2 py-1 rounded">
                                    Not Present
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {activeTab === 'keywords' && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-6">Keyword Analysis Details</h3>
                      <div className="space-y-6">
                        {results.results.map((result, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-bold text-gray-900">"{result.keyword}"</h4>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <div className="text-sm text-gray-600">Search Volume</div>
                                  <div className="font-bold text-gray-900">{result.searchVolume?.toLocaleString() || 'N/A'}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm text-gray-600">Difficulty</div>
                                  <div className="font-bold text-orange-600">{result.keywordDifficulty || 'N/A'}</div>
                                </div>
                              </div>
                            </div>
                            
                            {result.features && (
                              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                {Object.entries(result.features).map(([feature, data]) => (
                                  <div
                                    key={feature}
                                    className={`p-3 rounded-lg text-center ${
                                      data.present
                                        ? 'bg-green-50 border border-green-200'
                                        : 'bg-gray-50 border border-gray-200'
                                    }`}
                                  >
                                    <div className="text-xs font-medium text-gray-600 mb-1">
                                      {getSERPFeatureName(feature)}
                                    </div>
                                    {data.present ? (
                                      <CheckCircleIcon className="w-5 h-5 text-green-600 mx-auto" />
                                    ) : (
                                      <XCircleIcon className="w-5 h-5 text-gray-400 mx-auto" />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {result.organicResults && result.organicResults.length > 0 && (
                              <div className="mt-4">
                                <h5 className="font-medium text-gray-900 mb-2">Top Organic Results</h5>
                                <div className="space-y-2">
                                  {result.organicResults.slice(0, 3).map((organic, orgIndex) => (
                                    <div key={orgIndex} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                                      <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                                        <span className="text-xs font-bold text-blue-600">{organic.position}</span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">{organic.title}</div>
                                        <div className="text-xs text-gray-600">{organic.domain}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {!results && (
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Track Every SERP Feature That Matters
                </h2>
                <p className="text-xl text-gray-600 mb-12">
                  Our SERP analysis tool monitors your visibility across all major search features
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 text-left">
                  {[
                    'Featured Snippets',
                    'People Also Ask',
                    'Knowledge Panels',
                    'Local Pack',
                    'Image Pack',
                    'Video Carousel',
                    'Top Stories',
                    'Twitter Cards',
                    'Site Links',
                    'Reviews & Ratings',
                    'FAQ Schema',
                    'Recipe Cards',
                    'Shopping Results',
                    'Jobs Pack',
                    'Events',
                    'Flights',
                    'Hotels',
                    'Knowledge Graph'
                  ].map((feature) => (
                    <div key={feature} className="flex items-center gap-2 p-4 bg-white rounded-lg shadow">
                      <ChartBarIcon className="w-5 h-5 text-indigo-600" />
                      <span className="font-medium text-gray-900">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
        <FAQSection items={SERP_FAQ} />
      </div></>
  );
};

export default SERPAnalysisPage;
